/*
 * Copyright (C) 2021 Quartet Health, Inc - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 */

import { t } from '@lingui/macro';

const copy = {
  landing: {
    cta: {
      title: t`We're here to help you feel your best.`,
      subtitle: t`Quartet matches the right mental health care option for you, that's covered by your insurance.`,
      button: t`Let's get started`
    },

    heroImageAltText: t`A smiling woman`,

    header: {
      getStarted: t`Get started`,
      seeIfImCovered: t`See if I am covered`
    },

    scrollHeader: {
      getStarted: t`Get started`
    }
  },

  howQuartetWorks: {
    title: t`How Quartet works`,
    getStarted: t`Get started`,
    insurance: {
      title: t`Verify your insurance`,
      paragraph: t`We're currently working with insurance plans in CA, IL, LA, NC, NJ, PA, and WA to match patients to the right care.`
    },
    yourself: {
      title: t`Tell us about yourself`,
      paragraph: t`Answer a few short questions about your health and what you're looking for so we can find the care that's right for you.`
    },
    care: {
      title: t`Be matched to care, for free`,
      paragraph: t`We'll match you to the right mental healthcare provider based on your needs, preferences and insurance.`
    },
    provider: {
      title: t`Connect with your provider`,
      paragraph: t`Your matched provider will reach out to set up an appointment. Please note that co-pays or deductibles apply if you see a provider.`
    }
  },

  mentalHealth: {
    title1: t`Mental health`,
    title2: t` is `,
    title3: t`health`,
    paragraph1: t`About one in four people in America experience a mental health condition like anxiety or depression.`,
    paragraph2: t`At Quartet, we believe that mental healthcare is healthcare, and we're here to help you get the care that's right for you.`
  },

  whatToExpect: {
    title: t`What to expect`,
    getStarted: t`Get started with Quartet`,
    appointment: {
      title: t`The first appointment`,
      paragraph: t`This is a time where you can get to know your provider and share more about your health. If it doesn't feel like a good fit, let us know and we'll match you to another.`
    },
    goal: {
      title: t`Setting goals`,
      paragraph: t`Your provider will work with you to set goals for what you'd like to achieve. Some examples of goals include: working through grief, better managing stress, or increasing patience.`
    },
    care: {
      title: t`Continuing care`,
      paragraph: t`How long you continue care is up to you and your provider. As you begin to reach your treatment goals, you can choose to self-manage or continue care to reach new goals.`
    }
  },

  testimonial: {
    quote: t`Dealing with mental health can be hard, but the right person can help you address anxiety, anger, and fear, and develop coping skills to get through the toughest parts.`,
    author: 'Larue',
    alt: t`A woman smiling`
  },

  faq: {
    title: t`Frequently Asked Questions`,
    link: null as string,
    item1: {
      question: t`Is Quartet a mental health provider?`,
      answer: t`Quartet does not provide direct mental health care. We are a free service that works to find you care based on your needs.`
    },
    item2: {
      question: t`How will Quartet match me with a provider?`,
      answer: t`We match you with a licensed mental health provider or program that’s right for you based on your location, insurance, and health needs.`
    },
    item3: {
      question: t`Who's eligible for Quartet services?`,
      answer: t`Quartet is currently available to patients who have insurance coverage in the following states: CA, IL, LA, NC, NJ, PA, WA.`
    }
  },

  resources: {
    affiliateNotInScope: {
      modalTitle: t`Our curated provider options`,
      modalCopy1: t`Quartet only refers patients to licensed providers who have demonstrated the ability to meet a wide range of patient clinical needs, treat patients with various levels of acuity, have the capacity to service new patients, and meet your preference for virtual or in-person treatment.`,
      modalCopy2: t`Certain providers are offered the opportunity to participate in Quartet's affiliate program ("Affiliates") where, for a small fee, Quartet provides these providers with administrative and referral services and rigourously vets them to ensure they possess and maintain an even higher level of clinical capability, which includes the uniform practice of evidence-based treatment. While at all times maintaining patient confidentiality, Quartet regularly monitors all associated providers - Affiliates and non-Affiliates - to ensure that they deliver consistently high levels of care.`,
      modalCopy3: t`We exclude providers from participating if their clinical and operational practices fall outside of Quartet's standard of care, if they have demonstrated poor operational performance, or if they have been subject to substantiated disciplinary actions on their licenses, including exclusion from federal programs.`,
      modalButton: t`Yes, I understand`
    }
  },

  footer: {
    title: t`EMERGENCY SUPPORT`,
    paragraph: t`If you are in a crisis or if you or any other person may be in danger, don't use this site - `,
    call: t`call 911`,
    resource: t`These resources`,
    resourceUrl: t`https://patient.quartethealth.com/p/crisis-resources`,
    resourceLabel: t`These resources can also provide you with immediate help`,
    resourceHelp: t`can also provide you with immediate help`,
    privacy: t`Privacy Policy`,
    terms: t`Terms of Use`,
    termsLabel: t`Terms of Use`,
    language: t`Language Assistance`,
    languageLabel: t`Language Assistance`,
    provider: t`For Providers`,
    providerLabel: t`For Providers`
  },

  selectInsurance: {
    title: t`Select your insurance`,
    subTitle1: t`If you're not a member of a participating insurance plan and you're interested in Quartet, `,
    linkText: t`sign up here`,
    subTitle2: t` and we'll notify you when we expand our services.`
  },

  disclaimer: {
    subTitle1: t`Quartet prioritizes security to protect your health information. To learn more, take a look at our `,
    privacy: t`privacy policy `,
    privacyUrl: 'https://www.quartethealth.com/privacy-policy',
    subTitle2: t` and `,
    terms: t`terms of use`,
    termsUrl: 'https://www.quartethealth.com/terms-of-service'
  },

  insuranceDisclaimer: null as string,

  yourself: {
    title: t`Tell us about yourself`,
    subTitle: t`We'll use this information to see if you're eligible for our services.`,
    firstName: t`First name`,
    lastName: t`Last name`,
    preferredName: t`Preferred first name`,
    dateOfBirth: t`Date of birth`,
    gender: t`Gender`,
    email: t`Email`,
    OptumEmailDisclaimerTitle: t`Quartet can send me emails.`,
    OptumEmailDisclaimerBody: t`I agree to receive emails from Quartet, and acknowledge the emails may contain my health information which may indirectly relate to sensitive services like mental health and substance use disorder care. These emails will be sent unencrypted. There is a risk of interception or disclosure of the contents of these emails. I understand I can withdraw my consent at any time.`,
    phone: t`Phone number`,
    sms: t`Quartet can send me SMS messages about my mental health care.`,
    smsDisclaimer: t`Text HELP for help or STOP to cancel messages at any time. Msg and data rates may apply. Msg frequency varies.`,
    termsUrl: t`https://www.quartethealth.com/terms-of-service`,
    voicemail: t`Quartet can leave detailed voicemails.`,
    voicemailDisclaimer: t`This includes specific information about mental health providers and care.`,
    optional: t`(optional)`
  },

  experience: {
    title: t`What are you currently experiencing?`,
    subTitle: t`Check all that apply. Select at least one option.`,
    pediatricTitle: t`What is the patient currently experiencing?`,
    depression: t`Depression`,
    anxiety: t`Anxiety or panic attacks`,
    adhd: t`ADHD`,
    ptsd: t`Post-traumatic stress disorder`,
    sleepDisorder: t`Sleeping too much or too little`,
    drugHistory: t`History of drug or alcohol use`,
    drugCurrent: t`Current drug or alcohol use`,
    hallucinations: t`Hearing or seeing things other people don't`,
    stress: t`Stress or big life changes`,
    moodSwings: t`Mood swings`,
    griefLoss: t`Grief/Loss`,
    eatingDisorder: t`Eating too much or too little`,
    familyIssues: t`Marriage or family problems`,
    abuseCurrent: t`Current sexual, emotional or physical abuse`,
    abuseHistory: t`History of sexual, emotional or physical abuse`,
    baby: t`Recently had a baby or currently pregnant`
  },

  patientPreferences: {
    title: t`What are you looking for in a provider?`,
    subtitle: t`We'll do our best to take your preference into account.`,
    pediatricTitle: t`What is the patient looking for in a provider?`,
    pediatricSubtitle: t`We'll do our best to take their preferences into account.`,
    inPerson: t`In-person`,
    inPersonDesc: t`You'll visit a provider's office for one-on-one therapy.`,
    pediatricInPersonDesc: t`They'll visit a provider's office for one-on-one therapy.`,
    teleOnly: t`Virtually`,
    teleOnlyDesc: t`You'll meet with a provider using video services, like Zoom or Skype.`,
    pediatricTeleOnlyDesc: t`They'll meet with a provider using video services, like Zoom or Skype.`,
    teleAndInPerson: t`In-person and virtually`,
    teleAndInPersonDesc: t`You'll have the option to visit a provider's office and use video services.`,
    pediatricTeleAndInPersonDesc: t`They'll have the option to visit a provider's office and use video services.`,
    noPreference: t`No preference`
  },

  service: {
    title: t`What services are you interested in?`,
    subTitle: t`Check all that apply. Select at least one option.`,
    therapy: t`Talk Therapy`,
    medicationAndTherapy: t`Medication (may require talk therapy)`,
    drugsOrAlcohol: t`Medication-assisted treatment for drug or alcohol use`,
    unsure: t`I'm unsure. I'd like to talk with someone to learn more`
  },

  call: {
    title: t`What's the best time to call you?`,
    subTitle: t`Let us know the best time for your Quartet Care Navigator to get in touch by phone.`,
    anytime: t`Any time`,
    morning: t`Morning (9am to 12pm)`,
    earlyAfternoon: t`Early afternoon (12pm to 3pm)`,
    lateAfternoon: t`Late afternoon (3pm to 6pm)`
  },

  almostDone: {
    title: t`You're almost done`,
    subTitle: t`We just need a little more information so we can match you with the right care.`,
    addressSubtitle: t`We'll use the address you enter to match you with providers and online therapy options in your area.`,
    insuranceSubtitle: t`Find your Member ID number on your insurance card. `,
    modalLink: t`Where can I find my ID number?`,
    streetAddress: t`Street Address`,
    streetAddressLineTwo: t`Suite/Apt/Unit`,
    optional: t`(optional)`,
    city: t`City`,
    state: t`State`,
    postalCode: t`Zip code`
  },

  welcome: {
    title: t`Welcome, `,
    paragraph1: t`What's next:`,
    paragraph2: t`A Care Navigator from Quartet or your matched care provider will reach out by phone, email or text. They'll answer any questions you have and talk to you about what you're looking for.`,
    paragraph3: t`If you have any questions, feel free to call us at`,
    tel: t`(312) 815-9050`,
    telLink: t`tel:3128159050`
  },

  learnMore: {
    title: t`Learn more about mental health and what you can expect from care`,
    button: t`Learn more`
  },

  insuranceModal: {
    title: null as string
  },

  nonMember: {
    title: t`Don't have insurance through a participating insurance provider?`,
    subTitle: t`We're just getting started. Sign up with your email to be notified when we expand our services.`,
    firstName: t`First name`,
    lastName: t`Last name`,
    dateOfBirth: t`Date of birth`,
    insurance: t`Insurance`,
    email: t`Email`
  },

  nonMemberWelcome: {
    title: t`Thank you, `,
    paragraph1: t`We'll let you know when we expand our services to cover your insurance plan.`,
    paragraph2: t`In the meantime, follow the link below to learn more about mental health or `,
    link: t`check out these local mental health and social services resources.`,
    linkUrl: t`https://www.nami.org`
  }
};

export default copy;
